import { hardwareApi } from '../services/hardwareApi';
// The CODE128 encoder moved to utils/code128.ts — the orders screen draws the
// same symbol on screen, and two copies of a pattern table is one wrong digit
// away from a barcode that scans as a different id.
import { drawCode128 } from './code128';

/**
 * WAVE 4 — Rollo 2"×0.51" unit label, printed from `GET /units/{unitId}/label`.
 *
 * super-admin-ui has NEITHER `jsbarcode` nor `jspdf` in package.json (checked
 * — platform-app's product-label pipeline, `applications/platform-app/src/
 * utils/barcodeLabel.ts`, has both; this app has neither), and the working
 * rules forbid adding packages. So this is a PRINT-WINDOW HTML/canvas
 * fallback, not a port of that pipeline — a real, hand-rolled CODE128
 * (subset B) barcode drawn on an offscreen `<canvas>`, not the decorative
 * CSS bars UnitPage previously showed. It reuses that pipeline's layout
 * numbers (50.8mm × 12.7mm label, ~1.2mm edge margins) and its "no prices"
 * rule; it does not reuse its code, since jsPDF isn't available here.
 *
 * The generated `<img>` is embedded as a data URL in a plain popup window
 * with `@page` print CSS sized to the label, then `window.print()` is
 * called — the OS print dialog (pointed at the Rollo) does the rest, same
 * hand-off point as `barcodeLabel.ts`'s `doc.save(...)` download.
 */

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Fetch `GET /units/{unitId}/label`, render it, and open the browser's print
 * dialog. NO prices are ever drawn — the endpoint doesn't return any.
 */
export async function printUnitLabel(unitId: string): Promise<void> {
  const data = await hardwareApi.getUnitLabel(unitId);
  const trackingId = data.trackingId || data.unitId;
  const brandModel = [data.brand, data.modelName].filter(Boolean).join(' · ').toUpperCase() || 'DEVICE';
  const serial = data.serial ? `S/N ${data.serial}` : '';

  const barcodeCanvas = drawCode128(trackingId, 560, 90);
  const barcodeDataUrl = barcodeCanvas.toDataURL('image/png');

  const win = window.open('', '_blank', 'width=480,height=260');
  if (!win) {
    console.error('[unitLabelPrint] popup blocked — allow popups for this site to print labels');
    return;
  }

  win.document.write(`<!DOCTYPE html><html><head><title>Label ${escapeHtml(trackingId)}</title>
<style>
  @page { size: 50.8mm 12.7mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { width: 50.8mm; height: 12.7mm; font-family: 'Courier New', monospace; color: #000; background: #fff; padding: 1.2mm 1.5mm; }
  .row { display: flex; justify-content: space-between; align-items: baseline; font-size: 6.5px; font-weight: 700; letter-spacing: .03em; white-space: nowrap; overflow: hidden; }
  img.bc { display: block; width: 100%; height: 5mm; margin: 0.6mm 0; image-rendering: pixelated; }
  .tid { text-align: center; font-size: 7px; font-weight: 700; letter-spacing: .15em; }
</style></head>
<body>
  <div class="row"><span>${escapeHtml(brandModel)}</span><span>${escapeHtml(serial)}</span></div>
  <img class="bc" src="${barcodeDataUrl}" alt="barcode" />
  <div class="tid">${escapeHtml(trackingId)}</div>
  <script>
    window.onload = function () { setTimeout(function () { window.focus(); window.print(); }, 150); };
  </script>
</body></html>`);
  win.document.close();
}
